import React, { Dispatch, SetStateAction } from 'react';
import { Modifier } from 'react-day-picker';
import { DropdownOptionAPI } from '@ingestro/importer-react';

import { IBenefitGroup } from './BenefitGroups';
import { AutocompleteChangeValue, IAutocompleteOption } from 'edenred-ui';

export interface ICloseDetailDialog {
  isOpen: boolean;
  handleCloseModal: (isCancel?: boolean) => void;
  primaryAction: () => void;
  secondaryAction: () => void;
  secondaryActionText: string;
  title: string;
  primaryActionText: string;
  contentText: string;
  disableSubmit: boolean;
}

export interface IPersonalDataField {
  label: string;
  name: string;
  required?: boolean;
  disabled?: boolean;
  validate?: Array<() => unknown>;
  isAddMode?: boolean;
  isDataField?: boolean;
}

export interface IBeneficiaryDetailSlideout {
  expanded: boolean;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  beneficiary: IBeneficiaryData;
  editingMode: boolean;
  setEditingMode: Dispatch<SetStateAction<boolean>>;
  isProcessing: boolean;
  editEmployeeAction: (employee: any) => Promise<any>;
  isLunchBenefitActive: boolean;
}

export interface IBenefitGroupSettings {
  benefitType: string;
  deductionType: string;
  endDate: string;
  frequency: string;
  groupId: number;
  maxAmountPerPeriod: number;
  mobilePaymentEnabled: boolean;
  name: string | null;
  startDate: string;
}

export interface IBeneficiaryPayload {
  emailAddress: string;
  firstName: string;
  lastName: string;
  personId: string;
  personalId: string;
  costCenter: string;
  employeeNumber: string;
  floor: string;
  department: string;
  region: string;
  startOfBenefit: string;
  endOfBenefit?: string | null;
  EmploymentType: string;
  benefitPercentage: number | null;
  benefitLunchMaxWorkingDays: number | null;
  groupId: number;
  endOfFunds: string | null;
}

export interface IBeneficiaryData extends IBeneficiaryPayload {
  createdAt: string;
  employmentType: string;
  endOfBenefit: string | null;
  endOfFunds: string | null;
  benefitGroupIds: number[];
  benefitGroupSettings: IBenefitGroupSettings[];
  pendingLoadDate: string | null;
}

export interface IAddBeneficiarySlideout {
  expanded: boolean;
  isProcessing: boolean;
  currentlySetBenefitValues: ICurrentlySetBenefitValues | null;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  addEmployeeAction: (
    employee: any,
    addAnotherEmployee: boolean,
    resetForm: () => void
  ) => Promise<void | string>;
  isLunchBenefitActive: boolean;
}

export interface ICurrentlySetBenefitValues {
  cardLunch?: number;
  cardRecreational?: number;
  cardTransport?: number;
}

export interface IBenefits {
  isAddMode?: boolean;
}

export interface IAddBeneficiaryActions {
  isProcessing: boolean;
  addAnotherEmployee: boolean;
  disableSubmit: boolean;
  toggleAddAnotherEmployee: () => void;
  handleClose: () => void;
  handleSubmitFormExecute: () => void;
}

export interface ITerminationDateDialog {
  isOpen: boolean;
  handleClose: () => void;
  isAddMode?: boolean;
  handleSetDate: (value: Date | null) => void;
  handleSave: () => void;
  endOfBenefit: Date | null;
  disableSave: boolean;
  errorText?: string;
  startDate?: Date | string | null;
  title: string;
  name?: string;
}

export interface IEntitledDataDialog {
  isOpen: boolean;
  handleClose: () => void;
}

export interface ITerminationDate {
  showTerminationButton: boolean;
  isAddMode?: boolean;
}

export interface ITerminationDatePicker {
  dayValue?: Date | string | null;
  setTerminationDate: (value: Date | null) => void;
  isError: boolean;
  title?: string;
  disabledCustomDates?: Date | string | Modifier | Modifier[] | null;
  errorText?: string;
  disableByDefault?: boolean;
}

export interface IEntitledDataField {
  isAddMode?: boolean;
  isRemoved?: boolean;
}

export interface IContractPercentageField {
  disabled?: boolean;
  isAddMode?: boolean;
  required?: boolean;
  isPartTime?: boolean;
}

export interface IEmployeeTypeDataField {
  isAddMode?: boolean;
}

export interface IBeneficiariesTopSection {
  showBeneficiaryAddedNotification: boolean;
}

export interface IBeneficiary {
  benefitTypes: string[];
  emailAddress: string;
  endOfFunds: string | null;
  firstName: string;
  isNew: boolean;
  lastName: string;
  personId: string;
  personalId: string;
  employmentType: string;
  groupId: number;
  benefitPercentage: number;
}

export interface IContractedBenefits {
  id: number;
  type: string;
  state: string;
}

export interface IBeneficiaries {
  showDetail: (personId: string) => void;
}
export interface IBeneficiariesRow extends IBeneficiaries {
  beneficiary: IBeneficiary;
  groupSetting?: IBenefitGroup;
  isSelected: boolean;
  onToggleSelect: (personId: string) => void;
}

export interface IBeneficiariesTable extends IBeneficiaries {
  isLoading: boolean;
  beneficiaries: IBeneficiary[];
  allBeneficiaries: IBeneficiary[];
  groupSettings?: IBenefitGroup[];
}

type IBeneficiariesFiltersHandler = (
  event: React.SyntheticEvent<Element, Event>,
  newValue: AutocompleteChangeValue
) => void;

export interface IBeneficiariesFilters {
  employeesOptions: IAutocompleteOption[];
  selectedBenefitGroup?: IAutocompleteOption;
  benefitGroupOptions: IAutocompleteOption[];
  selectedEmploymentType?: IAutocompleteOption;
  employmentTypeOptions: IAutocompleteOption[];
  handleEmployeesChange: IBeneficiariesFiltersHandler;
  handleGroupChange: IBeneficiariesFiltersHandler;
  handleEmploymentTypeChange: IBeneficiariesFiltersHandler;
}
export interface IBeneficiaryActions {
  beneficiary: IBeneficiary;
  showDetail: (personId: string) => void;
  groupSetting?: IBenefitGroup;
  endOfFundsDatePassed: boolean;
}

export interface BeneficiaryFormValues {
  firstName: string;
  lastName: string;
  personId: string;
  personalId: string;
  emailAddress: string;

  employeeNumber: string;
  costCenter: string;
  floor: string;
  department: string;
  region: string;

  endOfBenefit: string;
  endOfFunds: string;

  EmploymentType: string;
  EmployeePercentage: number | '';
  DaysForLunari: number | '';

  createdAt: string;
  entitled: string;

  lunchBenefitActive: boolean | undefined;
  editing: boolean;
  benefitGroupIds?: number[];
  pendingLoadDate: string;
  groupId: number;
}

export type BenefitGroupSettingsResponse = {
  benefitGroupSettings: IBenefitGroupSettings[];
};

export interface IChangeGroupActions {
  allBeneficiaries: IBeneficiary[];
  selectedBeneficiaryIds: string[];
  disableChangeGroup: boolean;
  setSelectedBeneficiaryIds: (selectedBeneficiaryIds: string[]) => void;
}

export interface IChangeGroupModal {
  isOpen: boolean;
  handleClose: () => void;
  handleConfirm: (selectedGroupId: number) => void;
  changeInProgress: boolean;
  selectedGroupId?: number;
  setSelectedGroupId: (selectedGroupId: number) => void;
}

export interface IChangeGroupModalContent {
  selectedGroup: IBenefitGroup;
}

export enum FileFormatEnum {
  XLSX = 'xlsx',
  CSV = 'csv'
}

export interface IngestroImporterProps {
  groups: DropdownOptionAPI[];
  uploadInProgress: boolean;
  setUploadInProgress: (uploadInProgress: boolean) => void;
  requestId: number | null;
  setRequestId: (requestId: number | null) => void;
  allowNextNavigation?: () => void;
}

export type LeaveImportEmployeesModalProps = {
  isOpen: boolean;
  onStay: () => void;
  onLeave: () => void;
};
